import { hasPremiumWidgetAccess } from "./entitlement";
import { cachedApiGet } from "./persistent-cache";
import {
  closeGoogleIntegrationsRuntimeBoundary,
  publishGoogleIntegrationsRuntimeState,
} from "@/shared/google/google-integration-runtime.mjs";

export type PublicConfig = {
  authEnabled?: boolean;
  passkeysEnabled?: boolean;
  hasPremiumAccess?: boolean;
  premiumEntitled?: boolean;
  trialActive?: boolean;
  premiumPriceInr?: number | null;
  premiumCurrency?: string;
  playPriceMonthlyInr?: number | null;
  playPriceYearlyInr?: number | null;
  freeTrialDays?: number | null;
  freeTrialMinutes?: number | null;
  newsCategoryAudioAvailable?: boolean;
  newsStoryAudioAvailable?: boolean;
  narrationEnabled?: boolean;
  ga4MeasurementId?: string | null;
  googleIntegrationsEnabled?: boolean;
  googleClientId?: string | null;
  calendarEnabled?: boolean;
  calendarWriteEnabled?: boolean;
  watchlistFreeMaxStocks?: number | null;
  watchlistPremiumMaxStocks?: number | null;
};

let cached: PublicConfig | null = null;
let cachedAtEpochMs = 0;
let requestEpoch = 0;
const PUBLIC_CONFIG_MEMORY_TTL_MS = 30_000;

export function invalidatePublicConfigCache() {
  cached = null;
  cachedAtEpochMs = 0;
  requestEpoch += 1;
  closeGoogleIntegrationsRuntimeBoundary();
}

/** Fail-closed view used while foreground revalidation is in flight or stale fallback is served. */
export function projectPublicConfigGoogleIntegrationsOff(value: PublicConfig): PublicConfig;
export function projectPublicConfigGoogleIntegrationsOff(value: null): null;
export function projectPublicConfigGoogleIntegrationsOff(value: undefined): undefined;
export function projectPublicConfigGoogleIntegrationsOff(
  value: PublicConfig | null | undefined,
): PublicConfig | null | undefined {
  if (!value) return value;
  return {
    ...value,
    googleIntegrationsEnabled: false,
    googleClientId: null,
    calendarEnabled: false,
    calendarWriteEnabled: false,
  };
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  if (cached && Date.now() - cachedAtEpochMs < PUBLIC_CONFIG_MEMORY_TTL_MS) return cached;
  const capturedRequestEpoch = requestEpoch;
  const data = await cachedApiGet<PublicConfig>(
    ["public-config"],
    "/api/public-config",
    undefined,
    {
      ttlMs: 5 * 60 * 1000,
      fallbackCode: "public_config_failed",
      // A stale cached ON value must never reopen provider UI or OAuth after
      // the production kill switch has been turned off or cannot be fetched.
      fallbackTransform: projectPublicConfigGoogleIntegrationsOff,
      responseCommitGuard: () => capturedRequestEpoch === requestEpoch,
      uncommittedResponseTransform: projectPublicConfigGoogleIntegrationsOff,
    },
  );
  if (capturedRequestEpoch !== requestEpoch) {
    return projectPublicConfigGoogleIntegrationsOff(data);
  }
  publishGoogleIntegrationsRuntimeState(data.googleIntegrationsEnabled);
  cached = data;
  cachedAtEpochMs = Date.now();
  if (typeof window !== "undefined") {
    (window as Window & { TB_PUBLIC_CONFIG?: PublicConfig }).TB_PUBLIC_CONFIG = data;
  }
  return data;
}

/** @deprecated Prefer hasPremiumWidgetAccess — kept for call-site compat */
export function hasPremiumAccess(cfg: PublicConfig | null | undefined): boolean {
  return hasPremiumWidgetAccess(cfg);
}

export { hasPremiumWidgetAccess };
